import React, { createContext, useContext, useEffect, useState } from "react";
import { useInitialValues } from "./use-initial-values";
import { mapFiltersToKitQueryInput } from "../utils/mapping";
import { isEqual, omit } from "lodash";
import { UseSearchFilters, useSearchFilters } from "@bonliva-crm/ui/search-filters";
import { BookingKitQueryInput } from "@graphql";
import { BookingKitFilterState } from "../utils/booking-kit-filter-state";
import { parseSearchProfileFilters } from "../utils/parse-search-profile-filters";

export type KitFilter = BookingKitQueryInput;

type KitSearchFiltersReturnType = {
  queryInput: KitFilter;
  filterManager: UseSearchFilters<BookingKitFilterState>;
  /**
   * Form has non-empty value in any field
   */
  hasValues: boolean;
};

const KitSearchFiltersContext = createContext<KitSearchFiltersReturnType>(null!);

export const useBookingKitSearchFilters = () => {
  const context = useContext(KitSearchFiltersContext);

  if (context == null) {
    throw new Error(
      "useAccommodationRequestSearchFilters must be used within KitSearchFiltersProvider"
    );
  }

  return context;
};

export const KitSearchFiltersProvider = ({ children }: { children: any }) => {
  const { initialValues, emptyValues } = useInitialValues();

  const [queryInput, setQueryInput] = useState<KitFilter>(
    mapFiltersToKitQueryInput(initialValues)
  );

  const [hasValues, setHasValues] = useState(false);

  const filterManager = useSearchFilters({
    initialValues: initialValues,
    localStorageKey: "kitSearchFavoriteFields",
    overrides: {
      hasLegalPersonType: { excludeNoOption: true, name: "hasLegalPersonType" }
    },
    activeFilters: [
      "searchProfile",
      "responsiblePerson",
      "kitStatusFilter",
      "projectResponsiblePersonId",
      "areaOfExpertise",
      "consultantIds",
      "customerIds",
      "hasLegalPersonType",
      "costCenter",
      "managedBy",
      "groupInvoicing",
      "isNational",
      "consultantPayout",
      "bookingKitTagFilter",
      "bookingDate",
      "kitTypes"
    ],
    parseSearchProfileFilters,
    onChange: (value) => {
      setQueryInput(mapFiltersToKitQueryInput(value));
    }
  });

  const values = filterManager.formMethods.watch();

  useEffect(() => {
    const omitFields = ["sort", "text", "fullTextSearch"];
    setHasValues(!isEqual(omit(emptyValues, omitFields), omit(values, omitFields)));
  }, [emptyValues, values]);

  return (
    <KitSearchFiltersContext.Provider
      value={{
        queryInput,
        filterManager,
        hasValues
      }}
    >
      {children}
    </KitSearchFiltersContext.Provider>
  );
};
